import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Switch, Space, Tag, Popconfirm,
  message, Typography, Tabs, Drawer, Statistic, Row, Col, List, Avatar, Badge,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, MessageOutlined,
  BarChartOutlined, CopyOutlined, RobotOutlined, UserOutlined,
} from '@ant-design/icons';
import { chatbotAPI } from '../services/api';
import AlayaessAgentPicker, { AlayaessAgentPickerHint } from '../components/AlayaessAgentPicker';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function ChatbotAdminPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [sessionsDrawer, setSessionsDrawer] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [chatDrawer, setChatDrawer] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [statsModal, setStatsModal] = useState(null);
  const [stats, setStats] = useState(null);
  const [form] = Form.useForm();

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await chatbotAPI.list();
      setConfigs(res.data);
    } catch (e) {
      message.error('載入失敗: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleCreate = () => {
    setEditingConfig(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingConfig(record);
    form.setFieldsValue({
      ...record,
      allowed_topics: record.allowed_topics ? record.allowed_topics.join(', ') : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      if (typeof payload.allowed_topics === 'string') {
        payload.allowed_topics = payload.allowed_topics
          ? payload.allowed_topics.split(',').map(s => s.trim()).filter(Boolean)
          : null;
      }
      if (editingConfig) {
        await chatbotAPI.update(editingConfig.id, payload);
        message.success('已更新');
      } else {
        await chatbotAPI.create(payload);
        message.success('已建立');
      }
      setModalOpen(false);
      fetchConfigs();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await chatbotAPI.delete(id);
      message.success('已刪除');
      fetchConfigs();
    } catch (e) {
      message.error('刪除失敗: ' + e.message);
    }
  };

  const openSessions = async (config) => {
    setSessionsDrawer(config);
    setSessionsLoading(true);
    try {
      const res = await chatbotAPI.listSessions(config.id);
      setSessions(res.data);
    } catch (e) {
      message.error('載入對話失敗');
    } finally {
      setSessionsLoading(false);
    }
  };

  const openChat = async (configId, sessionId) => {
    try {
      const res = await chatbotAPI.getSession(configId, sessionId);
      setChatDrawer(res.data);
      setChatMessages(res.data.messages || []);
    } catch (e) {
      message.error('載入訊息失敗');
    }
  };

  const openStats = async (config) => {
    setStatsModal(config);
    try {
      const res = await chatbotAPI.getStats(config.id);
      setStats(res.data);
    } catch (e) {
      message.error('載入統計失敗');
    }
  };

  const copyEmbedCode = (token) => {
    const code = `<script src="${window.location.origin}/chatbot-widget.js" data-token="${token}"></script>`;
    navigator.clipboard.writeText(code);
    message.success('已複製嵌入碼');
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '名稱', dataIndex: 'name',
      render: (name, record) => (
        <Space>
          <RobotOutlined />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: '狀態', dataIndex: 'is_active', width: 80,
      render: (v) => v ? <Tag color="green">啟用</Tag> : <Tag>停用</Tag>,
    },
    { title: '對話數', dataIndex: 'session_count', width: 80 },
    {
      title: 'Embed Token', dataIndex: 'embed_token', width: 160,
      render: (token) => (
        <Space>
          <Text code style={{ fontSize: 11 }}>{token?.substring(0, 12)}...</Text>
          <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyEmbedCode(token)} />
        </Space>
      ),
    },
    {
      title: '操作', width: 280,
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>設定</Button>
          <Button size="small" icon={<MessageOutlined />} onClick={() => openSessions(record)}>對話</Button>
          <Button size="small" icon={<BarChartOutlined />} onClick={() => openStats(record)}>統計</Button>
          <Popconfirm title="確認刪除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>AI Chatbot 管理</Title>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增 Chatbot</Button>}
      >
        <Table
          columns={columns}
          dataSource={configs}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          size="middle"
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingConfig ? '編輯 Chatbot 設定' : '新增 AI Chatbot'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名稱" rules={[{ required: true }]}>
            <Input placeholder="例：產品顧問 Bot" />
          </Form.Item>
          <Form.Item name="welcome_message" label="歡迎訊息">
            <TextArea rows={3} placeholder="您好！有什麼我可以幫您的嗎？&#10;（支援多行，可設定較長的歡迎詞）" />
          </Form.Item>
          <Form.Item name="system_prompt" label="系統提示詞（角色設定）" tooltip="設定 AI 的角色、語氣、行為規範">
            <TextArea rows={6} placeholder="設定 AI 的角色和行為，例如：&#10;你是 Zoya，一位專業的 AI 教育訓練顧問。&#10;回答請用繁體中文，語氣友善專業。&#10;遇到不確定的問題請告知用戶聯繫客服。" />
          </Form.Item>
          <Form.Item name="product_context" label="教學內容（知識庫）" tooltip="輸入所有產品資訊、FAQ、教學材料，AI 會根據此內容回答問題" extra="可貼入大量產品說明、FAQ、教學文件，內容越詳細 AI 回答越準確">
            <TextArea rows={12} placeholder="在此輸入產品資訊與教學內容，例如：&#10;&#10;【產品概述】&#10;Aeon AI 教育訓練平台是一套完整的企業 AI 培訓解決方案...&#10;&#10;【核心功能】&#10;1. AI 課程生成 — 自動根據企業需求產生培訓課程&#10;2. 智能評估系統 — 即時追蹤學員學習進度&#10;3. 個人化學習路徑 — 根據學員程度自動調整內容&#10;&#10;【常見問題】&#10;Q: 支援哪些語言？&#10;A: 目前支援繁體中文與泰文&#10;&#10;Q: 如何開始使用？&#10;A: 請聯繫業務團隊進行 Demo 安排" />
          </Form.Item>
          <Form.Item name="allowed_topics" label="允許主題 (逗號分隔)">
            <Input placeholder="產品規格, 價格, 技術支援" />
          </Form.Item>
          <Form.Item name="scenario_id" label="關聯情境 ID">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="is_active" label="啟用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="alayaess_bot_name"
            label="AlayaEss Agent（Mode B 綁定）"
            tooltip="從 AlayaEss admin 註冊的 bot 中挑一個，這個 chatbot 之後產出的 AI 回應會用該 agent 的 persona。留空 = fallback Mode A（共用 Mika）。"
            extra={<AlayaessAgentPickerHint />}
          >
            <AlayaessAgentPicker />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sessions Drawer */}
      <Drawer
        title={`對話記錄：${sessionsDrawer?.name || ''}`}
        open={!!sessionsDrawer}
        onClose={() => { setSessionsDrawer(null); setSessions([]); }}
        width={600}
      >
        <Table
          dataSource={sessions}
          rowKey="id"
          loading={sessionsLoading}
          size="small"
          pagination={{ pageSize: 20 }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 50 },
            { title: '訪客', dataIndex: 'visitor_name', render: (v, r) => v || r.visitor_email || '匿名' },
            {
              title: '狀態', dataIndex: 'status', width: 70,
              render: (v) => <Badge status={v === 'active' ? 'processing' : 'default'} text={v === 'active' ? '進行中' : '已結束'} />,
            },
            { title: '訊息數', dataIndex: 'message_count', width: 70 },
            {
              title: '時間', dataIndex: 'created_at', width: 140,
              render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-',
            },
            {
              title: '', width: 60,
              render: (_, record) => (
                <Button size="small" icon={<MessageOutlined />} onClick={() => openChat(sessionsDrawer.id, record.id)}>查看</Button>
              ),
            },
          ]}
        />
      </Drawer>

      {/* Chat Messages Drawer */}
      <Drawer
        title={`對話詳情 #${chatDrawer?.id || ''}`}
        open={!!chatDrawer}
        onClose={() => { setChatDrawer(null); setChatMessages([]); }}
        width={500}
      >
        {chatDrawer && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">訪客：</Text>{' '}
            <Text>{chatDrawer.visitor_name || chatDrawer.visitor_email || '匿名'}</Text>
          </div>
        )}
        <List
          dataSource={chatMessages}
          renderItem={(msg) => (
            <List.Item style={{ padding: '8px 0', border: 'none' }}>
              <div style={{
                display: 'flex', flexDirection: 'column', width: '100%',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '80%',
                  padding: '8px 12px',
                  borderRadius: 12,
                  background: msg.role === 'user' ? '#1890ff' : '#f0f0f0',
                  color: msg.role === 'user' ? '#fff' : '#000',
                }}>
                  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>
                    {msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    {' '}{msg.role === 'user' ? '訪客' : 'AI'}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                </div>
                <Text type="secondary" style={{ fontSize: 10, marginTop: 2 }}>
                  {msg.created_at ? new Date(msg.created_at).toLocaleString('zh-TW') : ''}
                </Text>
              </div>
            </List.Item>
          )}
        />
      </Drawer>

      {/* Stats Modal */}
      <Modal
        title={`統計：${statsModal?.name || ''}`}
        open={!!statsModal}
        onCancel={() => { setStatsModal(null); setStats(null); }}
        footer={null}
        width={500}
      >
        {stats && (
          <Row gutter={[16, 16]}>
            <Col span={12}><Statistic title="總對話數" value={stats.total_sessions} /></Col>
            <Col span={12}><Statistic title="進行中" value={stats.active_sessions} /></Col>
            <Col span={12}><Statistic title="總訊息數" value={stats.total_messages} /></Col>
            <Col span={12}><Statistic title="平均訊息/對話" value={stats.avg_messages_per_session} /></Col>
          </Row>
        )}
      </Modal>
    </div>
  );
}
